// ─────────────────────────────────────────────────────────────────────────────
// VALID APP URLs
// ─────────────────────────────────────────────────────────────────────────────

import { IqeProjectStatus } from './interfaces/ui';
import { ProjectMappingType } from './interfaces/ui/addProjectModal';

export const VALID_APP_URLS = [
  'https://uat.devx.cbre.com/',
  'https://staging.devx.cbre.com/',
  'https://prod.devx.cbre.com/',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// FORM OPTIONS
// ─────────────────────────────────────────────────────────────────────────────

export const ENVIRONMENT_OPTIONS = [
  'Dev',
  'SIT',
  'QA',
  'UAT',
  'Non-Prod',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// FORM LABELS & MESSAGES
// ─────────────────────────────────────────────────────────────────────────────

export const PROJECT_MAPPING_OPTIONS: readonly ProjectMappingType[] = [
  'ADO',
  'JIRA',
];

export const VALIDATION_MESSAGES = {
  required: (fieldName: string) => `${fieldName} is required`,
  invalidUrl: 'Enter a valid URL',
  selectOption: (fieldName: string) => `Select ${fieldName}`,
  acknowledgement: 'You must acknowledge the terms to proceed',
} as const;

export const HELPER_MESSAGES = {
  create: 'Complete all required fields to create a project',
  edit: 'Complete all required fields to update a project',
} as const;

export const ACKNOWLEDGEMENT_TEXT =
  'By proceeding, you authorize the iQE Engine (Service Account: us_svc_devx_prod@cbre.com) to access your application using the credentials provided. This enables automated generation, execution, and reporting of test scenarios on your behalf.';

export const ACKNOWLEDGEMENT_LABEL = 'I acknowledge and agree to proceed';

export const DEFAULT_FORM_DATA = {
  projectName: '',
  appUrl: '',
  loginNotRequired: false,
  environment: '',
  projectMappingType: '' as ProjectMappingType,
  mappedProjectId: '',
  mappedProjectName: '',
  mappedProjectOrg: '',
  testPlanId: '',
  testPlanName: '',
  testSuiteId: '',
  testSuiteName: '',
  existingTestPlanId: '',
  existingTestPlanName: '',
  existingTestSuiteId: '',
  existingTestSuiteName: '',
  flowId: '',
  flowName: '',
};

// Transformation of API project status to UI project status
export const IQE_PROJECT_STATUS_MAP: Record<string, IqeProjectStatus> = {
  // CREATE operation statuses
  CREATE_SUCCESSFUL: 'SUCCESSFUL',
  CREATE_TRIGGERED: 'SUBMITTED',
  CREATE_INITIATED: 'SUBMITTED',
  CREATE_INPROGRESS: 'PROCESSING',
  CREATE_FAILED: 'FAILED',
  CREATE_FAILED_LOGIN: 'FAILED_LOGIN',

  // GENERATE operation statuses
  GENERATE_TRIGGERED: 'GENERATING',
  GENERATE_INPROGRESS: 'GENERATING',
  GENERATE_SUCCESSFUL: 'GENERATE_SUCCESSFUL',
  GENERATE_FAILED: 'GENERATE_FAILED',

  // EXECUTE operation statuses
  EXECUTE_INITIATED: 'EXECUTING',
  EXECUTE_INPROGRESS: 'EXECUTING',
  EXECUTE_SUCCESSFUL: 'EXECUTE_SUCCESSFUL',
  EXECUTE_FAILED: 'EXECUTE_FAILED',

  // ENHANCE operation statuses
  ENHANCE_INITIATED: 'ENHANCING',
  ENHANCE_INPROGRESS: 'ENHANCING',
  ENHANCE_SUCCESSFUL: 'ENHANCE_SUCCESSFUL',
  ENHANCE_FAILED: 'ENHANCE_FAILED',
};

export const IQE_PROJECT_STATUS_MESSAGE_MAP: Record<IqeProjectStatus, string> =
  {
    SUBMITTED: 'Project creation request submitted.',
    PROCESSING: 'Validating credentials and creating your project…',
    SUCCESSFUL: 'Project created successfully',
    FAILED_LOGIN:
      'Login validation failed — please check your credentials and try again',
    FAILED:
      'Project creation failed due to an unexpected error. Please try again',

    GENERATING: 'Generating test scenarios…',
    GENERATE_SUCCESSFUL: 'Test scenarios generated successfully',
    GENERATE_FAILED: 'Scenario generation failed. Please try again',

    EXECUTING: 'Running your test suite…',
    EXECUTE_SUCCESSFUL: 'Test execution completed',
    EXECUTE_FAILED: 'Test execution failed',

    ENHANCING: 'Enhancing test coverage…',
    ENHANCE_SUCCESSFUL: 'Test enhancement completed',
    ENHANCE_FAILED: 'Test enhancement failed',
  };

export const DEFAULT_EMPTY_STATE_MESSAGE =
  'No test executions yet. Run your first test suite to generate scenarios, validate workflows, and receive AI-powered testing insights.';
